'use client';
import { setAskLifecycleUploadEnabled } from '#pplx/frontend/apps/ask/o11y/askLifecycle.js';
import { canonicalizeLegacyPermalinkUrl, canonicalizeLegacyPermalinkUrlText, installLegacyPermalinkHistoryCanonicalizer, } from '#pplx/frontend/apps/ask/permalink-slug/canonicalizeLegacyPermalink.js';
import { regexToRoutes } from '#pplx/frontend/apps/ask/regex-to-routes/regex-to-routes.js';
import { getCometBrowserInfo } from '#pplx/frontend/comet/client/utils.js';
import { O11Y_BLOCK_LIST_ORG_IDS } from '#pplx/frontend/common/o11y/o11yBlockList.js';
import { redactTelemetryUrl } from '#pplx/frontend/common/redactAuthParams.js';
import { getNormalizedPath } from '#pplx/frontend/fetcher/core.js';
import { initialize as initializeLogger, setGlobalContext as setLoggerGlobalContext, } from '#pplx/frontend/logger/browser.js';
import { isRecentBuild } from '#pplx/frontend/logger/isRecentBuild.js';
import { redactSignedUrlText } from '#pplx/frontend/logger/redactSignedUrl.js';
import { getTimeOrigin, initialize as initializeMetrics, reportMetric, setGlobalContext as setMetricsGlobalContext, } from '#pplx/frontend/metrics/browser.js';
import { absolutePath, matchRoute, relativePath } from '#pplx/frontend/router/UrlUtils.js';
const REGION_HEADER = 'x-ppl-region';
let initialized = false;
const MODULE_EXEC_START_TS = performance.now();
const IS_XPLAT_DESKTOP_BUILD = Boolean(import.meta.env.VITE_IS_PERPLEXITY_DESKTOP_BUILD);
const DATADOG_CONFIG = IS_XPLAT_DESKTOP_BUILD
    ? {
        applicationId: 'f537b0e2-322e-4990-93a0-b8258693b08d',
        clientToken: 'pube5addbe924a52b1c911f9bbd1e1bf80a',
        service: 'perplexity-xplat-renderer',
    }
    : {
        applicationId: '4705c1a2-db1e-42f0-8a66-cd6208ae9e65',
        clientToken: 'pub4ecf63e3fd1ad28de1a9027c01181601',
        service: undefined,
    };
export function initializeClientO11y({ bootstrapConfig, user, routes, base = '', globalContext, enableRum = true, localLogLevel, }) {
    const isServer = typeof document === 'undefined';
    if (initialized || isServer) {
        return;
    }
    // Cached sessions initialize Datadog before React renders. Canonicalize here
    // as well so the initial view never captures a title-bearing legacy URL.
    canonicalizeLegacyPermalinkUrl(window.location, window.history);
    const version = bootstrapConfig.version;
    const env = bootstrapConfig.env ?? 'local';
    const cometBrowserInfo = getCometBrowserInfo();
    const isInternal = user?.email?.endsWith('@perplexity.ai');
    const debug = env === 'local';
    const sessionSampleRate = env === 'testing' || debug || !!cometBrowserInfo || isInternal ? 100 : 1;
    // Only turn on session replay for internal users or non-prod
    const sessionReplaySampleRate = debug || isInternal ? 100 : 0;
    const blockO11y = O11Y_BLOCK_LIST_ORG_IDS.includes(user?.org_uuid ?? '');
    setAskLifecycleUploadEnabled(Boolean(user) && !blockO11y);
    if (!blockO11y) {
        let botScore;
        const isLowBotScore = () => botScore != null && botScore >= 1 && botScore < 30;
        const is_recent_build = isRecentBuild(bootstrapConfig.minVersion);
        setO11yGlobalContext({
            isAuthenticated: Boolean(user),
            minVersion: bootstrapConfig.minVersion,
            webResourcesBuild: import.meta.env.VITE_WEB_RESOURCES_BUILD || undefined,
            is_recent_build,
        });
        if (globalContext) {
            setO11yGlobalContext(globalContext);
        }
        if (cometBrowserInfo) {
            setO11yGlobalContext({
                cometAgentExtensionVersion: cometBrowserInfo?.agent_extension_version,
                cometDeviceId: cometBrowserInfo.device_id,
                cometMainExtensionVersion: cometBrowserInfo.extension_version,
                cometVersion: cometBrowserInfo?.browser_version,
                erp: bootstrapConfig.erp,
                cometAndroidVersion: cometBrowserInfo?.android_version,
                cometIosVersion: cometBrowserInfo?.ios_version,
            });
        }
        if (enableRum) {
            initializeMetrics({
                applicationId: DATADOG_CONFIG.applicationId,
                clientToken: DATADOG_CONFIG.clientToken,
                version,
                env,
                debug,
                service: DATADOG_CONFIG.service,
                // Since we use RUM without limits, we can set the sample rate to 100%
                sessionSampleRate: 100,
                sessionReplaySampleRate,
                beforeSend: (event, context) => {
                    if (event.type === 'error' && isLowBotScore())
                        return false;
                    // OAuth callback URLs carry one-time credentials in their query
                    // string; signed downloads carry replayable credentials there too.
                    // Legacy permalinks can carry private titles in their paths.
                    event.view.url = sanitizeRumUrl(event.view.url);
                    if (event.view.referrer) {
                        event.view.referrer = sanitizeRumUrl(event.view.referrer);
                    }
                    if (event.type === 'resource') {
                        event.resource.url = sanitizeRumUrl(event.resource.url);
                    }
                    if (event.context && isFetchResourceContext(context, event)) {
                        const region = context.response?.headers?.get(REGION_HEADER);
                        if (region)
                            event.context.region = region;
                        const response = context.response;
                        const normalizedPath = response ? getNormalizedPath(response) : undefined;
                        if (normalizedPath) {
                            event.context.normalizedPath = normalizedPath;
                        }
                    }
                    if (routes && event.view.url && event.context) {
                        try {
                            const path = new URL(event.view.url).pathname;
                            const relPath = relativePath(path, base);
                            const matchedRoute = matchRoute(routes, relPath);
                            if (matchedRoute) {
                                if (matchedRoute.route.startsWith('^')) {
                                    const matchedRegexRoute = matchRoute(regexToRoutes(matchedRoute.route), relPath);
                                    if (matchedRegexRoute) {
                                        event.context.normalizedName = absolutePath(matchedRegexRoute.route, base);
                                    }
                                }
                                else {
                                    event.context.normalizedName = absolutePath(matchedRoute.route, base);
                                }
                            }
                        }
                        catch {
                            // Safe to ignore
                        }
                    }
                    return true;
                },
            });
        }
        // Datadog observes history synchronously and retains each view's original
        // URL as context for later events. Install this after RUM so legacy titles
        // are removed before its history wrapper receives SPA destinations.
        installLegacyPermalinkHistoryCanonicalizer();
        initializeLogger({
            clientToken: DATADOG_CONFIG.clientToken,
            version,
            env,
            debug,
            debugLogLevel: localLogLevel,
            sessionSampleRate,
            service: DATADOG_CONFIG.service,
            beforeSend: () => !isLowBotScore(),
        });
        if (enableRum && !IS_XPLAT_DESKTOP_BUILD) {
            void fetch('/api/bot-assessment', {
                cache: 'no-store',
                redirect: 'error',
                signal: AbortSignal.timeout(5_000),
            })
                .then((response) => {
                if (!response.ok)
                    throw new Error('Bot assessment unavailable');
                return response.json();
            })
                .then(({ score, verifiedBot, jsDetectionPassed, corporateProxy }) => {
                botScore = score;
                setO11yGlobalContext({
                    botAssessment: {
                        score,
                        verifiedBot,
                        jsDetectionPassed,
                        corporateProxy: corporateProxy ?? null,
                    },
                });
            })
                .catch(() => {
                // Optional context: challenges and network failures must not disrupt startup.
            });
        }
        if (enableRum) {
            reportMetric('web.frontend.module_exec_start', {
                startTime: getTimeOrigin(),
                duration: MODULE_EXEC_START_TS,
                is_first_page: true,
            });
        }
    }
    initialized = true;
}
function setO11yGlobalContext(context) {
    setLoggerGlobalContext(context);
    setMetricsGlobalContext(context);
}
function sanitizeRumUrl(url) {
    return canonicalizeLegacyPermalinkUrlText(redactSignedUrlText(redactTelemetryUrl(url)));
}
function isFetchResourceContext(context, event) {
    return (event.type === 'resource' &&
        event.resource.type === 'fetch' &&
        'isManual' in context &&
        !context.isManual);
}
//# sourceMappingURL=initializeClientO11y.js.map